import * as moment from "moment";

import { IImportOption } from "./interfaces/IImportOption";
import { IImportValidationError } from "./interfaces/IImportValidation";
import { IRowCol } from "./interfaces/IImportAdapterResult";
import { IValidationResult } from "./interfaces/IValidationResult";

import { FieldType } from "./enums/FieldType";
import { TranslationService } from "@services/translation.service";

export class CsvImporterService {

    static $inject = ['translationService']

    constructor(private readonly translationService: TranslationService) {}

    getTitleRowCellValues(csvLines: string[]): string[] {
        const titleRow = csvLines.length > 0 ? csvLines[0] : "";
        return this.getFieldValuesFromLine(titleRow);
    }

    getFieldValuesFromLine(line: string): string[] {
        const cellValues = line.split(/,(?=(?:[^\"]*\"[^\"]*\")*(?![^\"]*\"))/);

        // ,(?=x - Matches ',' only if ',' is followed by 'x'. This is called a lookahead.
        // (?:x) - Matches 'x' but does not remember the match. The parentheses are called non-capturing parentheses,
        //         and let you define subexpressions for regular expression operators to work with. Used to group the pattern.
        // [^\"] - negated or complemented character set. That is, it matches anything that is not enclosed in the brackets
        // x(?!y) - Matches 'x' only if 'x' is not followed by 'y'.This is called a negated lookahead.
        // [^ xyz] - A negated or complemented character set. That is, it matches anything that is not enclosed in the brackets. 

        // , char, followed by positive match on
        // (Any character that is not a backslash or quote), zero or any number of times, followed by a quote)
        // followed by any char other than backslash or quote, any number of times)
        // zero or more times for previous segment or parenthesized pattern,
        // followed by something that does not match the pattern:
        // (Any character other than a backslash or quote), zero or any number of times, followed by a quote

        if (cellValues.length < 3) {
            return [];
        }

        for (let i = 0; i < cellValues.length; i++) {
            cellValues[i] = cellValues[i].replace(/(^")|("$)/g, "");
        }

        if (cellValues[cellValues.length - 1] === "") {
            cellValues.splice(length - 1, 1);
        }

        return cellValues;
    }

    getInvalidFieldForTypeMessage(option: IImportOption, validationMessage: string): string {
        const validationText = validationMessage ? validationMessage : option.validationText;
        const errorText = (validationText)
            ? this.translationService.t(`csvImporterService.invalidValue`, validationText)
            : this.translationService.t(`csvImporterService.invalidType`, this.toNonTechEnglish(this.formatPascalCase(FieldType[option.fieldType])));

        return errorText;
    }

    extractLines(csvString: string): string[] {
        const csvLines = csvString.split(/(\r\n|\n)/);
        if (csvLines) {
            return csvLines.filter(csvLine => this.hasCellValues(csvLine));
        }
        return csvLines;
    }

    setTitleOption(columnName: string, option: IImportOption, cellValues: string[]): boolean {
        for (let cellIdx = 0; cellIdx < cellValues.length; cellIdx++) {
            const cellValue = cellValues[cellIdx].toLowerCase().replace(/[\s_\.]/g, "");
            if (cellValue === columnName || this.matchesAlias(cellValue, option.aliases)) {
                option.indexPosition = cellIdx;
                option.mappedColumnIdx = cellIdx;
                option.isValid = true;
                option.repeatCount++;

                return true;
            }
        }

        return false;
    }

    mapImportOptionFromTitles(option: IImportOption, cellValues: string[], validationErrors: IImportValidationError[], checkOnly = false): void {
        const columnName = option.columnName.toLowerCase().replace(/[\s_]/g, "");;

        if (!this.setTitleOption(columnName, option, cellValues) && (option.isRequired || option.isRequired === undefined)) {
            option.isValid = false;
            if (!checkOnly) {
                this.addOptionNotMappedError(option, validationErrors);
            }
            return;
        }

        option.isValid = true;
    }

    addHeaderValidationError(validationErrors: IImportValidationError[], fieldName: string, fieldValue: string, validationText: string) {
        this.addError(validationErrors, fieldName, fieldValue, validationText, 1, null, null);
    }

    addValidationError(validationErrors: IImportValidationError[], fieldName: string, fieldValue: string, validationText: string, rowIndex: number, rowColTranslations: IRowCol[][], colIndex?: number) {
        const firstRowOffset = 2;  // convert index to number and add 1 for removed header line
        const rowNo = rowIndex + firstRowOffset;
        this.addError(validationErrors, fieldName, fieldValue, validationText, rowNo, rowColTranslations, colIndex);
    }

    addError(validationErrors: IImportValidationError[], fieldName: string, fieldValue: string, validationText: string, rowNo: number, rowColTranslations: IRowCol[][], colIndex?: number) {
        let colNo = colIndex || colIndex === 0 ? colIndex + 1 : "";
        if (rowColTranslations) {
            const rowCol = this.getPreprocessTranslatedRowNo(rowNo, rowColTranslations, colIndex);
            rowNo = rowCol.row;
            colNo = rowCol.colFrom;
        }
        const colStr = colNo || colNo === 0 ? colNo.toString() : "-";
        validationErrors.push({
            rowNumber: rowNo,
            colNumber: colStr,
            fieldName: fieldName,
            fieldValue: fieldValue,
            text: validationText
        });
    }

    isEmptyCell(cellValue: string): boolean {
        return !cellValue || !cellValue.trim();
    }

    isValidFieldForType(fieldValue: string, fieldType: FieldType): IValidationResult {
        switch (fieldType) {
            case FieldType.String:
                return this.isValidString(fieldValue);
            case FieldType.DateOrInteger:
                return this.isValidDateOrNumber(fieldValue);
            case FieldType.Date:
                return this.isValidDate(fieldValue);
            case FieldType.Integer:
                return this.isValidInteger(fieldValue);
            case FieldType.PositiveDouble:
                return this.isValidPositiveDouble(fieldValue);
            case FieldType.Double:
                return this.isValidDouble(fieldValue);
            case FieldType.Boolean:
                return this.isValidBoolean(fieldValue);
        }

        return { isValid: false, message: "" };
    }

    isValidInteger(fieldValue: string): IValidationResult {

        const isNumber = /^\d+$/.test(fieldValue);

        const isValid = isNumber && !this.isValidDate(fieldValue).isValid;

        return { isValid: isValid, message: "" }
    }

    private matchesAlias(cellValue: any, aliases: string[]): boolean {
        for (let aliasIdx = 0; aliases && aliasIdx < aliases.length; aliasIdx++) {
            if (aliases[aliasIdx].trim().toLowerCase() === cellValue) {
                return true;
            }
        };
        return false;
    }

    private addOptionNotMappedError(option: IImportOption, validationErrors: IImportValidationError[]) {
        this.addHeaderValidationError(validationErrors, option.displayName, "Not found", this.translationService.t("csvImporterService.columnNotFound"));
    }

    private getPreprocessTranslatedRowNo(rowNumber: number, rowColTranslations: IRowCol[][], colIndex?: number): IRowCol {
        const rowCols = rowColTranslations[rowNumber - 1];

        if (rowCols.length > 0 && (colIndex || colIndex === 0) && rowCols[colIndex]) {
            return rowCols[colIndex];
        }

        return {
            row: rowNumber,
            colFrom: (colIndex || 0) + 1
        };
    }

    private isValidDate(fieldValue: string): IValidationResult {

        const fieldAsMoment = moment(fieldValue);

        let isValid = fieldAsMoment.isValid();

        if (!isValid) {
            return { isValid: false, message: "" };
        }

        isValid = fieldAsMoment.isAfter(moment());
        if (!isValid) {
            return { isValid: false, message: fieldAsMoment.isBefore(moment().startOf("day")) ? this.translationService.t("csvImporterService.dateBeforeCurrent") : this.translationService.t("csvImporterService.dateTimeBeforeCurrent") };
        }

        isValid = fieldAsMoment.isBefore(moment("2050-01-01"));
        if (!isValid) {
            return { isValid: false, message: this.translationService.t("csvImporterService.dateInFuture" )};
        }

        return { isValid: true, message: "" };;
    }

    private isValidDoubleCanBeNegative(fieldValue: string): boolean {
        const isNumber = /^[-+]?\d+\.\d+$/.test(fieldValue) || /^[-+]?\d+$/.test(fieldValue);
        return isNumber && !this.isValidDate(fieldValue).isValid;
    }

    private isValidDoubleNonNegative(fieldValue: string): boolean {
        const isNumber = /^[+]?\d+\.\d+$/.test(fieldValue) || /^[+]?\d+$/.test(fieldValue);
        return isNumber && !this.isValidDate(fieldValue).isValid;
    }

    private isValidPositiveDouble(fieldValue: string): IValidationResult {
        const isValid = this.isValidDoubleNonNegative(fieldValue) && !this.isValidDate(fieldValue).isValid;

        const message = isValid || !this.isValidDoubleCanBeNegative(fieldValue) ? "" : this.translationService.t("csvImporterService.negativeNumber");

        return { isValid: isValid, message: message };
    }

    private isValidDouble(fieldValue: string): IValidationResult {

        const isValid = this.isValidDoubleCanBeNegative(fieldValue) && !this.isValidDate(fieldValue).isValid;

        return { isValid: isValid, message: "" };
    }

    private isValidBoolean(fieldValue: string): IValidationResult {
        const fieldValueLower = fieldValue.toLowerCase();
        const isValid = fieldValueLower === "false" ||
                        fieldValueLower === "true" ||
                        fieldValueLower === "1" ||
            fieldValueLower === "0";
        return { isValid: isValid, message: "" };
    }

    private isValidDateOrNumber(fieldValue: string): IValidationResult {
        const dateValidationResult = this.isValidDate(fieldValue);
        const intValidationResult = this.isValidInteger(fieldValue);

        const isValid = dateValidationResult.isValid || intValidationResult.isValid;
        const message = dateValidationResult.message ? dateValidationResult.message : intValidationResult.message;
        return { isValid: isValid, message: message };
    }

    private isValidString(fieldValue: string): IValidationResult {
        const isValid = !this.isValidDate(fieldValue).isValid;
        return { isValid: isValid, message: "" };
    }

    private formatPascalCase(name: string) {
        return name.replace(/([A-Z])/g, " $1").toLowerCase().trim();
    }

    private toNonTechEnglish(name: string) {
        return name
            .replace(/double/, "floating point number")
            .replace(/integer/, "number")
            .replace(/boolean/, "true or false");
    }

    private hasCellValues(line: string): boolean {
        const cellValues = line.split(/,(?=(?:[^\"]*\"[^\"]*\")*(?![^\"]*\"))/);

        return cellValues && cellValues.length > 2 && cellValues.find(x => x.trim().length > 0) !== undefined;
    }
}

